<template>
	<div class="w-full">
		<GridLayout :sidebar-state="false">
			<template #right-header>
				<div class="w-full h-full flex items-center gap-3">
					<button
						class="btn btn-rounded"
						@click="Previous"
					>
						<i class="fas fa-chevron-left"></i>
					</button>
					<Steps
						:steps-map="stepsMap"
						:current-step="currentStep"
						@select-step="
							currentStep = $event
							autoNav = false
						"
					/>
					<FavoriteRoute />
				</div>
			</template>
			<template #right-content>
				<transition
					name="fade"
					mode="out-in"
				>
					<div v-if="step === 'pre'"></div>

					<Patient
						v-if="step === 'patient'"
						:patient-id="reception_flow_data.patient_id || null"
						:auto-nav="autoNav"
						:patient="selected.patient"
						class="w-full flex-1 flex flex-col"
						@next="WalkStep"
						@change:autoNav="autoNav = $event"
					/>

					<div
						v-if="step === 'schedulings'"
						class="w-full h-full flex flex-col"
					>
						<ToolBar>
							<div class="w-4/12 flex items-center py-1 gap-1">
								<SearchGroup
									:search="search"
									custom-classes="text-uppercase"
									@change="search = $event"
								/>
							</div>
						</ToolBar>
						<MainContent>
							<NewDatatable
								:value="newSchedulings"
								class="w-full flex-1"
								:headers="headers"
								:search="search"
								search-column="name"
								item-key="itemKey"
								@click="WalkStep"
							>
								<template #row="{ item }">
									<div class="datatable-cell">
										<b>
											{{ item.nome_paciente }}
										</b>
									</div>
									<div class="datatable-cell">
										{{
											item.desconhecido
												? item.tipo && item.numero
													? `${item.tipo} ${item.numero}`
													: 'SEM IDENTIFICAÇÃO'
												: `${item.tipo} ${item.numero}`
										}}
									</div>
									<div class="datatable-cell">
										{{ item.schedulings.length.toString().padStart(2, '0') }}
									</div>
									<div class="datatable-cell">
										{{ item.schedulings[0]?.partnerships[0]?.nome.toUpperCase() ?? 'SEM CONVÊNIO' }}
									</div>
								</template>
							</NewDatatable>
						</MainContent>
					</div>

					<MainContent
						v-if="step === 'register'"
						class="w-full flex-1"
					>
						<PatientIdentification
							class="px-4"
							:patient="selected.patient"
							@on-update-avatar="patient.avatar = $event"
						/>

						<PatientForm
							ref="patient_form"
							origin="confirmation"
							class="px-4"
							:patient="selected.patient"
							@next="WalkStep"
						/>
					</MainContent>

					<Partnership
						v-if="step === 'partnership'"
						ref="partnerships_form"
						type="confirmation"
						class="w-full flex-1 flex flex-col"
						:modality_id="scheduling.modality_id"
						:partnership="scheduling.partnership"
						@next="WalkStep"
						@partnership-change="selectedPartnershipId = $event"
						@on-load-partnerships="partnerships = $event"
					/>

					<Guide
						v-if="step === 'guide'"
						ref="guide"
						type="confirmation"
						class="w-full flex-1 flex flex-col"
						:modality_id="scheduling.modality_id"
						:schedulings="selected.schedulings"
						:partnerships="partnerships"
						:partnership="scheduling.partnership"
						:billing="scheduling.billing"
						:indicator="scheduling.indicator"
						:patient="selected.patient"
						:require_validation="true"
						@next="WalkStep"
						@billing-change="scheduling.billing = $event"
						@indicator-change="scheduling.indicator = $event"
					/>

					<Proceedings
						v-if="step === 'proceedings'"
						ref="proceedings"
						:schedulings="selected.schedulings"
						:patient="selected.patient"
						@next="WalkStep"
						@can-confirm-change="proceedingsCanConfirm = $event"
						@indicator-change="proceedingsIndicator = $event"
						@all-selected-change="allProceedingsSelected = $event"
					/>
				</transition>
			</template>
			<template #right-footer>
				<div class="w-full h-full flex justify-end items-center gap-3">
					<div class="w-full h-full flex justify-start items-center">
						<button
							v-if="isReceptionFlow"
							class="btn btn-danger btn-fixed-size"
							@click="CancelFlow()"
						>
							SUSPENDER
						</button>
					</div>
					<div class="w-full h-full flex justify-center items-center">
						<button
							class="btn btn-fixed-size"
							:disabled="step !== 'register'"
							@click="
								UnregisteringPatient()
								Previous()
							"
						>
							DEVOLVER
						</button>
					</div>
					<div class="w-full h-full flex justify-end items-center">
						<button
							v-if="isReceptionFlow && reception_flow_data.is_finished"
							class="btn btn-success btn-fixed-size"
							@click="FinishFlow()"
						>
							CONCLUIR
						</button>
						<button
							v-else
							class="btn btn-success btn-fixed-size"
							type="button"
							:disabled="continueButtonDisabled || step === 'proceedings'"
							@click="Continue"
						>
							{{ step === 'proceedings' ? 'CONFIRMAR' : 'AVANÇAR' }}
						</button>
					</div>
				</div>
			</template>
		</GridLayout>
	</div>
</template>

<script>
	const _ = require('lodash')
	import { mapGetters, mapActions } from 'vuex'
	import { SCHEDULE_TYPES } from '@/constants'
	import { useError, useConfirmation } from '@/composables'
	import receptionFlowMixin from '@/mixins/receptionFlow'

	import Partnership from '@/components/shared/scheduling/Partnership'
	import Guide from '@/components/shared/scheduling/Guide'
	import Patient from '@/components/shared/scheduling/Patient'
	import Proceedings from './components/Proceedings'

	export default {
		name: 'ConfirmWizard',
		mixins: [receptionFlowMixin],
		components: {
			Partnership,
			Guide,
			Patient,
			Proceedings,
		},
		data() {
			return {
				search: '',
				edit: true,
				schedulings: [],
				headers: [
					{ text: 'NOME', value: 'nome_paciente' },
					{ text: 'ID', value: 'numero', width: '10vw' },
					{
						text: 'PROCEDIMENTOS',
						value: 'schedulings.length',
						width: '15vw',
					},
					{ text: 'CONVÊNIO', value: 'partnership' },
				],
				selected: {
					patient: null,
					schedulings: [],
					parsed_schedulings: [],
					payment_methods_info: {},
					indicator: null,
				},
				scheduling: {
					partnership: null,
					billing: null,
					indicator: null,
				},
				partnerships: [],
				sidebarState: false,
				selectedPartnershipId: null,
				proceedingsCanConfirm: false,
				proceedingsIndicator: null,
				currentStep: 0,
				stepsMap: [
					{
						key: 'pre',
					},
					{
						key: 'schedulings',
						number: 1,
						label: 'AGENDAMENTOS',
						isValid: false,
					},
					{
						key: 'register',
						number: 2,
						label: 'CADASTRO',
						isValid: false,
					},
					{
						key: 'partnership',
						number: 3,
						label: 'CONVÊNIO',
						isValid: false,
					},
					{
						key: 'guide',
						number: 4,
						label: 'GUIA',
						isValid: false,
					},
					{
						key: 'proceedings',
						number: 5,
						label: 'PROCEDIMENTOS',
						isValid: false,
					},
				],
				finishButtonLabel: 'CONTINUAR',
				autoNav: true,
			}
		},
		computed: {
			...mapGetters('auth', ['requester_selected_id']),
			...mapGetters('system', ['showSidebar']),
			step() {
				return this.stepsMap[this.currentStep].key
			},
			continueButtonDisabled() {
				switch (this.step) {
					case 'schedulings':
						return true
					case 'register':
						return false
					case 'partnership':
						return !this.selectedPartnershipId
					case 'guide':
						return false
					case 'proceedings':
						return !this.proceedingsCanConfirm
					case 'confirm':
						return false
					default:
						return true
				}
			},
			newSchedulings() {
				return this.schedulings.map(scheduling => {
					scheduling.itemKey = `${scheduling.id}-${scheduling.schedulings[0]?.partnerships[0]?.id}`

					return scheduling
				})
			},
		},
		watch: {
			step: {
				handler(val) {
					let hash = ''

					switch (val) {
						case 'patient':
							hash = '#paciente'
							break
						case 'schedulings':
							this.selected.schedulings = []
							this.selected.parsed_schedulings = []

							this.GetSchedulingsToConfirm()

							hash = '#agendados'
							break
						case 'register':
							this.selected.parsed_schedulings = []
							hash = '#cadastro'

							this.GetModulesAndModalities({
								schedule_type: SCHEDULE_TYPES.PRE_APPOINTMENT,
								check_unavailable: true,
								with_instructions: true,
							})
							break
						case 'partnership':
							this.selected.parsed_schedulings = []
							this.selectedPartnershipId = this.scheduling.partnership?.id ?? null
							hash = '#convenio'
							break
						case 'guide':
							hash = '#guia'
							break
						case 'proceedings':
							this.proceedingsCanConfirm = false
							this.allProceedingsSelected = false
							hash = '#procedimentos'
							break
					}

					window.location.hash = hash
				},
				immediate: true,
			},
			$route(to) {
				if (this.isReceptionFlow) {
					switch (to.hash) {
						case '#paciente':
							this.currentStep = 1
							break
						case '#agendados':
							this.currentStep = 2
							break
						case '#cadastro':
							this.currentStep = 3
							break
						case '#convenio':
							this.currentStep = 4
							break
						case '#guia':
							this.currentStep = 5
							break
						case '#procedimentos':
							this.currentStep = 6
							break
					}
				} else {
					switch (to.hash) {
						case '#agendados':
							this.currentStep = 1
							break
						case '#cadastro':
							this.currentStep = 2
							break
						case '#convenio':
							this.currentStep = 3
							break
						case '#guia':
							this.currentStep = 4
							break
						case '#procedimentos':
							this.currentStep = 5
							break
					}
				}
			},
		},
		methods: {
			...mapActions('system', ['StartLoading', 'FinishLoading', 'GetModulesAndModalities']),
			ResetWizard() {
				this.selected = {
					patient: null,
					schedulings: [],
					parsed_schedulings: [],
					payment_methods_info: {},
					indicator: null,
				}

				this.scheduling = {
					partnership: null,
					billing: null,
					indicator: null,
				}

				this.stepsMap = this.stepsMap.map(step => {
					step.isValid = false
					return step
				})
			},
			Previous() {
				switch (this.step) {
					case 'schedulings':
						if (this.isReceptionFlow) {
							this.currentStep--
						} else {
							this.$router.push({ name: 'Home' })
						}
						break
					case 'patient':
					case 'register':
					case 'partnership':
					case 'guide':
					case 'proceedings':
						this.currentStep--
						break
					default:
						this.$emit('previous')
				}

				this.autoNav = false
			},
			async WalkStep(param = null) {
				switch (this.step) {
					case 'pre':
						this.ResetWizard()

						if (!this.isReceptionFlow) await this.GetSchedulingsToConfirm()

						this.currentStep++
						break
					case 'patient':
						this.selected.patient = param.patient
						this.selected.schedulings = []

						this.stepsMap[this.currentStep].isValid = true
						if (param.autoWalkStep) this.currentStep++
						break
					case 'schedulings':
						this.selected.schedulings = param.schedulings.map(scheduling => {
							scheduling.partnership = scheduling.partnerships[0]
							scheduling.selected_partnership = scheduling.partnerships[0]
							scheduling.billing = scheduling.partnerships[0]?.pivot ?? null

							return scheduling
						})

						this.selected.patient = {
							...param,
							schedulings: null,
						}

						this.scheduling.partnership = param.schedulings[0]?.partnerships[0] ?? null
						this.scheduling.billing = param.schedulings[0]?.partnerships[0]?.pivot ?? null
						this.scheduling.indicator = param.schedulings[0]?.indicator_doctor ?? null
						this.scheduling.modality_id = param.schedulings[0]?.proceeding?.modality_id ?? null

						this.RegisteringPatient()

						this.stepsMap[this.currentStep].isValid = true
						this.currentStep++
						break
					case 'register':
						this.selected.patient = param.patient

						this.stepsMap[this.currentStep].isValid = true
						this.currentStep++
						break
					case 'partnership':
						this.scheduling.partnership = param.partnership ?? this.scheduling.partnership

						this.stepsMap[this.currentStep].isValid = true
						this.currentStep++
						break
					case 'guide':
						this.scheduling.billing = {
							...(this.scheduling.billing || {}),
							...(param.billing || {}),
						}

						this.scheduling.indicator = param.indicator ?? this.scheduling.indicator

						this.selected.schedulings = this.selected.schedulings.map(scheduling => {
							scheduling.partnership = param.partnership ?? scheduling.partnership
							scheduling.selected_partnership = param.partnership ?? scheduling.selected_partnership
							scheduling.billing = {
								...(scheduling.billing || {}),
								...(param.billing || {}),
							}
							scheduling.indicator_doctor = this.scheduling.indicator ?? scheduling.indicator_doctor

							return scheduling
						})

						this.stepsMap[this.currentStep].isValid = true
						this.currentStep++
						break
					case 'proceedings':
						switch (param.method) {
							case 'confirm':
								this.selected.parsed_schedulings = param.schedulings
								this.selected.payment_methods_info = param.payment_methods_info
								this.selected.indicator = param.indicator

								await this.ConfirmSchedulings()
								break
							case 'cancel':
								this.selected.parsed_schedulings = param.schedulings

								await this.CancelSchedulings()
								break
							case 'fit':
								this.selected.parsed_schedulings = param.schedulings

								this.FitSchedulings()
								break
							case 'reschedule':
								this.selected.parsed_schedulings = param.schedulings

								this.Rescheduling()
								break
						}
						break
				}

				this.autoNav = true
			},
			CancelSchedulings() {
				this.StartLoading()

				return window.api
					.call('post', '/cancel-schedulings', {
						schedulings: this.selected.parsed_schedulings,
						organization_id: this.requester_selected_id,
					})
					.then(({ data }) => {
						if (data.response) {
							useConfirmation()

							if (this.selected.parsed_schedulings.length >= this.selected.schedulings.length) {
								this.currentStep = 0
								this.WalkStep()
							} else {
								this.selected.schedulings = this.selected.schedulings.filter(scheduling => {
									return !this.selected.parsed_schedulings.find(s => s.id == scheduling.id)
								})

								this.selected.parsed_schedulings = []
							}
						} else {
							useError('HOUVE UM ERRO AO CONCLUIR, CONTATE O SUPORTE')
						}
					})
					.finally(() => {
						this.FinishLoading()
					})
			},
			ConfirmSchedulings() {
				this.StartLoading()

				return window.api
					.call('post', '/confirm-schedulings', {
						schedulings: this.selected.parsed_schedulings,
						payment_info: this.selected.payment_methods_info,
						indicator: this.selected.indicator,
					})
					.then(({ data }) => {
						if (data.response) {
							useConfirmation()

							if (this.selected.parsed_schedulings.length < this.selected.schedulings.length) {
								this.selected.schedulings = this.selected.schedulings.filter(scheduling => {
									return !this.selected.parsed_schedulings.find(s => s.id == scheduling.id)
								})

								return
							}

							this.reception_flow_data.is_finished = true
							this.currentStep = 0
							this.WalkStep()
						} else {
							useError('HOUVE UM ERRO AO CONCLUIR, CONTATE O SUPORTE')
						}
					})
					.finally(() => {
						this.FinishLoading()
					})
			},
			async GetSchedulingsToConfirm() {
				this.StartLoading()

				return window.api
					.call('post', '/get-schedulings-to-confirm', {
						account_id: this.requester_selected_id,
						patient_id: this.selected.patient?.id ?? null,
					})
					.then(({ data }) => {
						if (!data.response) {
							useError('HOUVE UM ERRO AO OBTER, CONTATE O SUPORTE')
							return
						}

						this.schedulings = _.sortBy(data.schedulings, ['scheduling_timestamp', 'name'], ['asc', 'asc'])

						if (this.schedulings.length === 1 && this.autoNav && this.isReceptionFlow) {
							this.WalkStep(data.schedulings[0])
						}
					})
					.finally(() => {
						this.FinishLoading()
					})
			},
			FitSchedulings() {
				const scheduling = this.selected.parsed_schedulings

				this.$router.push({
					name: 'ReFitWizard',
					params: {
						scheduling_id: scheduling.id,
						source: 'ConfirmWizard',
					},
					query: {
						scheduling_data: JSON.stringify({
							id: scheduling.id,
							billing: scheduling.billing ?? null,
							partnership: scheduling.selected_partnership ?? scheduling.partnership ?? null,
							indicator: this.proceedingsIndicator ?? this.scheduling.indicator ?? null,
						}),
					},
				})
			},
			Rescheduling() {
				const schedulings = this.selected.parsed_schedulings

				this.$router.push({
					name: 'ReschedulingWizard',
					params: {
						source: 'ConfirmWizard',
					},
					query: {
						schedulings: JSON.stringify(schedulings.map(s => s.id ?? s)),
						schedulings_data: JSON.stringify(
							schedulings.map(s => ({
								id: s.id ?? s,
								billing: s.billing ?? null,
								partnership: s.selected_partnership ?? s.partnership ?? null,
								indicator: this.proceedingsIndicator ?? this.scheduling.indicator ?? null,
							}))
						),
					},
				})
			},
			async Continue() {
				if (this.step === 'register') {
					this.$refs.patient_form.Continue()
				} else if (this.step === 'partnership') {
					this.$refs.partnerships_form.Continue()
				} else if (this.step === 'guide') {
					this.$refs.guide.Continue()
				} else if (this.step === 'proceedings') {
					this.$refs.proceedings.Confirm()
				}
			},
			async SavePatient() {
				await this.$refs.patient_form.SavePatient()

				this.edit = false
			},
			async RegisteringPatient() {
				return window.api.call('post', '/registering-patient', {
					organization_id: this.requester_selected_id,
					schedulings: this.selected.schedulings,
				})
			},
			async UnregisteringPatient() {
				return window.api.call('post', '/unregistering-patient', {
					organization_id: this.requester_selected_id,
					schedulings: this.selected.schedulings,
				})
			},

			FixStepsMap() {
				if (this.isReceptionFlow) {
					this.stepsMap = this.stepsMap.map(step => {
						if (step.key === 'pre') return step

						step.number += 1

						return step
					})

					this.stepsMap.splice(1, 0, {
						key: 'patient',
						number: 1,
						label: 'PACIENTE',
						isValid: false,
					})
				}
			},
		},
		async beforeMount() {
			await this.HandleReceptionFlow()
			await this.FixStepsMap()

			this.WalkStep()
		},
	}
</script>

<style lang="scss" scoped>
	:deep(.new-datatable) {
		max-height: 42.1rem;
	}
</style>
